import React, { useState } from 'react';
import { useApi } from '../../hooks/useApi';

// ─── Manager Connection modal (T2M Kanban, Sprint 21.3, 9/28/26) ────────────
// Per docs/COMS_T2M_Kanban_Design_Decisions_2026-09-28.md — a required badge,
// not a stage. Real rule: must happen after the trial's real midpoint
// (start + (end-start)/2), computed per-trial since length varies (30-day
// legacy, 15-day current). BK: "the manager should also be able/willing to
// ask for the membership" — reflected in the copy below, not a separate flow.
// Posts to the existing logManagerConnection (queries.js) via the new
// FD-inclusive /t2m/kanban/manager-connection/{id} route — same write path
// the old t2mManagerLog route already used, just widened access.

const LOGGED_BY_KEY = 'coms_t2m_manager_connection_logged_by';

export default function T2MManagerConnectionModal({ card, onClose, onSaved }) {
  const { put } = useApi();
  const [connectedBy, setConnectedBy] = useState(() => {
    try { return sessionStorage.getItem(LOGGED_BY_KEY) || ''; } catch { return ''; }
  });
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function handleNameChange(v) {
    setConnectedBy(v);
    try { sessionStorage.setItem(LOGGED_BY_KEY, v); } catch { /* ignore */ }
  }

  async function handleSave() {
    if (!connectedBy.trim()) { setError('Enter the manager\'s name — club logins are shared, we need to know who made this connection.'); return; }
    setSaving(true);
    setError(null);
    try {
      await put(`/t2m/kanban/manager-connection/${card.id}`, { connectedBy: connectedBy.trim(), notes });
      onSaved();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460, width: '100%' }}>
        <div className="modal-title">
          🤝 Manager Connection — <span style={{ color: '#facc15' }}>{card.name}</span>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 14, lineHeight: 1.4 }}>
          Confirm a manager has made a personal connection with this trial past the midpoint of their trial.
          This is a good moment to make the membership ask directly, if it feels right — log that here too.
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#f97316', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Manager Name * <span style={{ fontWeight: 400, textTransform: 'none', color: '#666' }}>(club login is shared)</span>
          </div>
          <input
            type="text"
            value={connectedBy}
            onChange={e => handleNameChange(e.target.value)}
            placeholder="Manager's name"
            style={{ width: '100%', background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '7px 8px', fontSize: 12, boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Notes (optional)
          </div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="How'd the conversation go? Did you make the membership ask?"
            style={{
              width: '100%', background: '#111', border: '1px solid #333',
              color: '#ccc', borderRadius: 5, padding: '7px 8px', fontSize: 12,
              resize: 'none', height: 56, fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
        </div>

        {error && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{error}</div>}

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : '✓ Log Connection'}
          </button>
        </div>
      </div>
    </div>
  );
}
